import type {
  PremiumCalculatorReportInputs,
  PremiumCalculatorReportSnapshot,
  PremiumProfitReportInputs,
  PremiumProfitReportSnapshot,
} from "@/lib/core/types/analysis-log";
import type { Costs } from "@/lib/core/types/cost";
import type { Profit } from "@/lib/core/types/profit";
import type { AuctionSummary } from "@/lib/core/types/result";
import type { Valuation } from "@/lib/core/types/valuation";

export type ReportViewKeyType = "profit" | "rights_summary";

export function buildReportViewStorageKey(
  reportType: ReportViewKeyType,
  entityId: string,
  date = new Date(),
): string {
  const today = date.toISOString().split("T")[0];
  return `premium_report_view_${today}_${reportType}_${entityId}`;
}

export function parseSimulationId(
  simulationId: string,
): { caseNo: string; disposalSeq: number } {
  const [caseNo = "", disposalSeqString = "1"] = simulationId.split("_");
  return {
    caseNo,
    disposalSeq: parseInt(disposalSeqString, 10) || 1,
  };
}

export function resolveReportRouteParams(args: {
  simulationId: string;
  caseNo?: string;
  disposalSeq?: string;
}): { caseNo: string; disposalSeq: number } {
  if (args.caseNo && args.disposalSeq) {
    return {
      caseNo: args.caseNo,
      disposalSeq: parseInt(args.disposalSeq, 10) || 1,
    };
  }

  return parseSimulationId(args.simulationId);
}

export function createPremiumProfitReportInputs(args: {
  bidPrice: number;
  sellPrice?: number;
}): PremiumProfitReportInputs {
  return {
    reportType: "profit",
    bidPrice: args.bidPrice,
    sellPrice: args.sellPrice,
    ltv: undefined,
    interestRate: undefined,
    holdingPeriod: undefined,
  };
}

export function createPremiumProfitReportSnapshot(args: {
  profit: Profit;
  valuation: Valuation;
  costs: Costs;
  summary: AuctionSummary;
}): PremiumProfitReportSnapshot {
  return {
    reportType: "profit",
    profit: args.profit,
    valuation: args.valuation,
    costs: args.costs,
    summary: args.summary,
  };
}

export function createProfitReportSaveRequest<TInputs, TSnapshot>(args: {
  simulationId: string;
  caseNo: string;
  disposalSeq: number;
  inputs: TInputs;
  snapshot: TSnapshot;
}) {
  return {
    simulationId: args.simulationId,
    reportType: "profit" as const,
    reportData: {
      caseNo: args.caseNo,
      disposalSeq: args.disposalSeq,
      inputs: args.inputs,
      snapshot: args.snapshot,
    },
  };
}

export function buildProfitProcessingUrl(args: {
  caseNo: string;
  disposalSeq: number;
  bidPrice: number;
  sellPrice: number;
  reportId: string;
}): string {
  const encodedCaseNo = encodeURIComponent(args.caseNo);
  const params = new URLSearchParams({
    bidPrice: String(args.bidPrice),
    sellPrice: String(args.sellPrice),
    reportId: args.reportId,
    reportType: "profit",
  });

  return `/analysis/${encodedCaseNo}/${args.disposalSeq}/premium/processing?${params.toString()}`;
}

export function buildAnalysisDetailHref(
  caseNo: string,
  disposalSeq: number,
): string {
  return `/analysis/${encodeURIComponent(caseNo)}/${disposalSeq}`;
}

export function buildDashboardCalculatorHref(): string {
  return "/dashboard?section=calculator";
}

export function createReportSavedEventDetail(args: {
  reportType: "profit" | "calculator";
  caseNo: string;
  disposalSeq: number;
  logId: string;
}) {
  return {
    reportType: args.reportType,
    caseNo: args.caseNo,
    disposalSeq: args.disposalSeq,
    logId: args.logId,
  };
}

export function createPremiumCalculatorReportInputs(
  args: PremiumCalculatorReportInputs,
): PremiumCalculatorReportInputs {
  return args;
}

export function createPremiumCalculatorReportSnapshot(
  args: PremiumCalculatorReportSnapshot,
): PremiumCalculatorReportSnapshot {
  return args;
}

export function createCalculatorReportSaveRequest<TInputs, TSnapshot>(args: {
  simulationId: string;
  caseNo: string;
  disposalSeq: number;
  inputs: TInputs;
  snapshot: TSnapshot;
}) {
  return {
    simulationId: args.simulationId,
    reportType: "calculator" as const,
    reportData: {
      caseNo: args.caseNo,
      disposalSeq: args.disposalSeq,
      inputs: args.inputs,
      snapshot: args.snapshot,
    },
  };
}
